import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTestingStore } from "@/stores/testingStore";

/**
 * Thin selector over `testingStore`'s self-heal slice (see useExecutePlan for the rationale).
 *
 * A heal PROPOSES; it never re-runs the plan or changes the source run's verdict. So this hook
 * exposes a proposal + its status, NOT a second `ExecuteResult` — accept/reject is what actually
 * changes anything, and both always clear the proposal (the server clears its copy on every
 * answered outcome, including 404/409).
 */
export function useHealRun() {
	const queryClient = useQueryClient();
	const events = useTestingStore((s) => s.healEvents);
	const isStreaming = useTestingStore((s) => s.healStreaming);
	const error = useTestingStore((s) => s.healError);
	const status = useTestingStore((s) => s.healStatus);
	const proposal = useTestingStore((s) => s.healProposal);
	const planId = useTestingStore((s) => s.healPlanId);
	const notes = useTestingStore((s) => s.healNotes);
	const declineReason = useTestingStore((s) => s.healDeclineReason);
	const stage = useTestingStore((s) => s.healStage);
	const triageCategory = useTestingStore((s) => s.healTriageCategory);
	const deciding = useTestingStore((s) => s.healDeciding);

	const healAction = useTestingStore((s) => s.heal);
	const acceptAction = useTestingStore((s) => s.acceptHeal);
	const rejectAction = useTestingStore((s) => s.rejectHeal);
	const abort = useTestingStore((s) => s.healAbort);
	const reset = useTestingStore((s) => s.healReset);

	const heal = useCallback(
		(runId: string) => healAction(runId, queryClient),
		[healAction, queryClient],
	);
	const accept = useCallback(
		(targetPlanId: string) => acceptAction(targetPlanId, queryClient),
		[acceptAction, queryClient],
	);
	const reject = useCallback(
		(targetPlanId: string) => rejectAction(targetPlanId, queryClient),
		[rejectAction, queryClient],
	);

	return {
		heal,
		accept,
		reject,
		abort,
		reset,
		events,
		isStreaming,
		error,
		status,
		proposal,
		planId,
		notes,
		declineReason,
		stage,
		triageCategory,
		deciding,
		/** True when a patch set is waiting on the user. */
		hasProposal: status === "proposed" && proposal !== null,
	};
}
